/**
 * Created by fers on 05.02.16.
 */
import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';

import {declareEditEntity} from '../../common/fw/MetaDataHelper.ts';
import EditorWithSaver from '../fw/EditorWithSaver.react';
import EditorSaver from '../fw/EditorSaver';
import {injectIntl, FormattedMessage, defineMessages, IntlShape} from 'react-intl';
import urlMapping from '../app/URLMapping';
import types from '../../common/model-api/types';
import config from '../../common/config';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import ScenarioBoxAsWizard from '../scenarios/ScenarioBoxAsWizard.react';
import ScenarioIcon from '../scenarios/ScenarioIcon.react';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import "../scenarios/Scenario.scss";
import { User } from '../../common/types/types';

const messages = defineMessages({
    title: {id: 'channelOptionEdit.title', 'defaultMessage': 'Editing application option'},
    save: {id: 'action.saveOptions', 'defaultMessage': 'Save options'},
    options: {id: 'channelOptionEdit.options', 'defaultMessage': 'Options'}
});

interface FlowChannelConfigProps {
    intl: IntlShape;
    user: User;
}

class FlowChannelConfig extends React.Component<FlowChannelConfigProps> {

    static propTypes = {
        intl: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
    };
    typeName = 'flows';
    static contextTypes = {
        router: PropTypes.object
    };

    constructor(props) {
        super(props);

        let flowDef = this.props.user.organisation.flow.flowDef;
        let {flowChannelDefs} = flowDef;

        let {flow} = this.props.user.organisation;
        let {selectedChannels} = flow;

        /* we receive options as JSON string */
        this.originalOptions = { ...JSON.parse(selectedChannels) };

        for (let key in this.originalOptions) {
            this.originalOptions[key + "_id"] = this.originalOptions[key];
        }

        this.metadata = this.collectMetaData(flowDef, flowChannelDefs.edges.map(({node}) => node));
    }

    collectMetaData(flowDef, flowChannelDefs) {
        let list = {};

        let channels = this.props.user.organisation.channels.edges.map(({node}) => ({...node, id: node.realId}));

        for (let opt of flowChannelDefs) {
            let description = "";

            if (opt['disableAdding']) {
                continue;
            }

            if (opt['description']) {
                description = opt['description'];
            }

            let channelsToAdd = opt['channelDef_id'] ? channels.filter((channel) => channel['channelDef_id'] === opt['channelDef_id']) : channels;
            channelsToAdd = channelsToAdd.filter((channel) => channel['deleted'] !== true);
            if (channelsToAdd && channelsToAdd.length > 0) {
                channelsToAdd = channelsToAdd.sort((a, b) => {
                    let aName = a.channelDef.name;
                    let bName = b.channelDef.name;
                    if (a.name) {
                        aName = aName + " - " + a.name;
                    }
                    if (b.name) {
                        bName = bName + " - " + b.name;
                    }
                    return aName < bName ? -1 : 1;
                });
            }

            list[opt.key] = {
                name: opt['name'],
                type: 'relation',
                to: () => types['channels'],
                description: description,
                values: channelsToAdd,
                display: (item) => {
                    if (item['name']) {
                        return item['channelDef']['name'] + " - " + item['name'];
                    } else {
                        return item['channelDef']['name'];
                    }
                },
            };
        }

        return declareEditEntity({
            entity: "options",
            graphQL: "Options",
            name: this.props.intl.formatMessage(messages.options),
            fields: (types) => {
                return list;
            }
        });
    }

	// eslint-disable-next-line max-params
    _saver = (metadata, isCreate, columns, data) => {
        /* we have to find which options are authentication and which not */
        let {flow} = this.props.user.organisation;

        let fatQuery = Relay.QL `fragment on UpdateFlowPayload {
                                updatedNode {
                                    id,
                                    selectedChannels,
                                    activated,
                                    invalid,
                                    hasInvalidChannel,
                                    channels(first: 100) {
                                        edges {
                                            node {
                                                id,
                                            }
                                        }
                                    },
                                },
                            }`;

        let mutation = Relay.QL `mutation { updateFlow }`;


		// eslint-disable-next-line max-params
        let saver = new EditorSaver(metadata, mutation, fatQuery, null, isCreate ? EditorSaver.CREATE : null, (data, storeData, defaultValue, columns) => {

            for (let key of Object.keys(data)) {
                let val = data[key] ? String(data[key]).trim() : null;

                data[key] = val;
            }

            storeData['selectedChannels'] = JSON.stringify({...JSON.parse(flow.selectedChannels), ...data});
            storeData['id'] = flow['id'];

            return storeData;
        });

        return saver;
    };

    _onSuccess = () => {
        let {flow} = this.props.user.organisation;

        let path = urlMapping.getUrl(types['flows'], "view", flow);
        this.context.router.push(path);
    };

    render() {
        let {flow} = this.props.user.organisation;
        let flowDef = flow.flowDef;

        let options = this.originalOptions;

        return (
            <AppPanel breadcrumbParams={[
                {type: types['flows'], data: flow},
                {type: types['scenarios'], data: this.props.user.scenario},
                {type: types['organisations'], data: this.props.user.organisation}]
            } title={this.props.intl.formatMessage(messages.title)}>
					<h2>{flowDef.name}</h2>

					<p><FormattedMessage defaultMessage="Please select application to use with {flowName}."
										id="flowChannelConfig.description1"
										values={{
                                            flowName: flowDef.name,
                                            productName: config.PRODUCT_NAME
                                        }}
					/></p>

                    <EditorWithSaver
						buttonTitle={this.props.intl.formatMessage(messages.save, {'entity': this.metadata.pluralName()})}
						data={options}
						metadata={this.metadata}
						onSuccess={this._onSuccess}
						saver={this._saver}
						urlMapping={urlMapping}
					>
					</EditorWithSaver>
            </AppPanel>
        );
    }
}


export default Relay.createContainer(wrapNotFound(injectIntl(FlowChannelConfig), (props) => {
    return props.user.organisation && props.user.organisation.flow;
}), {
    initialVariables: {
        organisationId: '',
        flowId: '',
        scenarioId: '',
        scenarioSelected: false,
    },
    prepareVariables (variables) {
		if (!variables.organisationId) {
			variables.organisationId = getLastOrganisationId();
		}

        variables['scenarioSelected'] = variables['scenarioId'] ? true : false;

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                scenario(id: $scenarioId) @include(if: $scenarioSelected) {
                    id,
                    realId,
                    ${ScenarioBoxAsWizard.getFragment('scenario')},
                    ${ScenarioIcon.getFragment('scenario')}
                },
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    users(first: 2) {
                        edges {
                            node {
                                id,
                                realId
                            }
                        }
                    },
                    channels(first: 1000, deleted: false) {
                        edges {
                            node {
                                id,
                                realId,
                                name,
                                channelDef_id,
                                deleted,
                                channelDef {
                                    id,
                                    realId,
                                    name,
                                    icon
                                }
                            }
                        }
                    },
                    ${ScenarioBoxAsWizard.getFragment('organisation')},
                    flow(id: $flowId) {
                        id,
                        realId,
                        organisation_id,
                        activated,
                        flowDef {
                            id,
                            realId,
                            name,
                            type,
                            flowChannelDefs(first: 100) {
                                edges {
                                    node {
                                        id,
                                        key,
                                        name,
                                        description,
                                        channelDef_id,
                                        doNotShowToUser,
                                        disableAdding,
                                        channelDef {
                                            id,
                                            realId,
                                            name,
                                            icon,
                                        }
                                    }
                                }
                            }
                        },
                        selectedChannels,
                        enabled
                    }
                },
            }
        `,
    },
});
